import { useState, useEffect, useRef, useCallback } from 'react';
import { C, FONT } from './adminShared';

// ── DROP-OFF CHART ───────────────────────────────────────────────────────────
// A retention curve on the CARD'S OWN MEASURE (2026-10-01): at each 30-second
// mark t, the people whose TIME ACTUALLY SEEN is at least t — the same unique
// seconds, exact or estimated, that the card's "N% seen" bars count. So the
// curve can only fall, and at 25/50/75/90/95% it is the card's bar count
// (the endpoint's `seenCurve`, computed beside the bars from the same people).
//
// It used to plot who saw ANY of each 30-second segment. That is not drop-off:
// someone who skipped to the end lit up the last mark, someone who saw 97% but
// stopped just short of it did not — so the chart could show more people at
// 18:00 than the card had at 95%+ (31 vs 27, 14d paid, 1 Oct).
//
// Each point carries TWO shares: of the people who started watching (the
// card's "Started watching" tile — the line is drawn on this one) and of
// everyone the card's bars divide by (its unique visitors).
//
// WHY A MODAL AND NOT INLINE: the Pages tab is three narrow columns. A 30-plus
// point line needs real width to be readable at all, and the columns cannot
// give it. The modal borrows the whole viewport for as long as it is open.
//
// NO CHARTING LIBRARY. The frontend has none installed (deps are react,
// react-dom, react-router-dom, axios) and adminShared.js already hand-rolls its
// charts in raw SVG. This follows that house pattern rather than adding a
// dependency for one screen.
//
// THE NUMBERS ARE NOT SMOOTHED. Every mark the API returns is plotted as it is.

const SMALL_SAMPLE = 30;   // Ben expects this to start meaning something at 100–300+

// The SVG is drawn at MEASURED PIXEL WIDTH rather than scaled from a fixed
// viewBox. A viewBox scaled down to 375px would shrink the axis text with it,
// to roughly 5px. Measuring instead keeps every label at its real size on a
// phone, which is where Ben reads this.
const useMeasuredWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

const pctOf = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
const fmtMark = (secs) => `${Math.floor(secs / 60)}:${String(Math.floor(secs) % 60).padStart(2, '0')}`;

const RetentionChart = ({ points, started, landed, lengthSecs, marker }) => {
  const [wrapRef, width] = useMeasuredWidth();
  const [hover, setHover] = useState(null);

  const H = 300;
  const M = { top: 16, right: 14, bottom: 40, left: 42 };
  const plotW = Math.max(0, width - M.left - M.right);
  const plotH = H - M.top - M.bottom;

  const n = points.length;
  // Time axis, 0 to the video's length, so the 95% marker sits where it
  // belongs between two 30-second marks. The marks themselves stay evenly
  // spaced, exactly as before.
  const span = Math.max(lengthSecs || 0, points[n - 1]?.secs || 0, 1);
  const xAt = (secs) => (secs / span) * plotW;
  const yAt = (p) => plotH - (Math.max(0, Math.min(100, p)) / 100) * plotH;
  const gap = n > 1 ? xAt(points[1].secs) - xAt(points[0].secs) : plotW;

  // One label per ~62px, so they never collide at 375px.
  const step = Math.max(1, Math.ceil(n / Math.max(3, Math.floor(plotW / 62))));

  if (!width) return <div ref={wrapRef} style={{ width: '100%', height: H }} />;

  const mx = marker ? xAt(marker.secs) : null;
  const markerLeft = mx != null && mx > plotW * 0.6;   // label on the side with room

  const tip = hover != null ? points[hover] : null;
  const TIP_W = 196;

  return (
    <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
      <svg
        width={width}
        height={H}
        role="img"
        aria-label={`Retention curve across ${n} thirty-second marks, based on ${started} people who started watching`}
        style={{ display: 'block', touchAction: 'pan-y' }}
        onMouseLeave={() => setHover(null)}
      >
        <g transform={`translate(${M.left},${M.top})`}>
          {[0, 25, 50, 75, 100].map(g => (
            <g key={g}>
              <line x1={0} x2={plotW} y1={yAt(g)} y2={yAt(g)} stroke={C.border} strokeWidth="1" />
              <text x={-8} y={yAt(g) + 4} textAnchor="end" fontSize="11" fill={C.accent}>{g}%</text>
            </g>
          ))}

          {/* The card's last bar, on the chart: 95% of the video's length. */}
          {marker && (
            <g>
              <line x1={mx} x2={mx} y1={0} y2={plotH} stroke={C.cta} strokeWidth="1.5" strokeDasharray="4 3" />
              <text x={markerLeft ? mx - 5 : mx + 5} y={yAt(100) + 12} textAnchor={markerLeft ? 'end' : 'start'}
                fontSize="10.5" fontWeight="700" fill={C.heading}>
                {marker.label}
              </text>
              <text x={markerLeft ? mx - 5 : mx + 5} y={yAt(100) + 25} textAnchor={markerLeft ? 'end' : 'start'}
                fontSize="10" fill={C.accent}>
                {fmtMark(marker.secs)} · {marker.count} of {started}
              </text>
            </g>
          )}

          <polyline points={points.map(pt => `${xAt(pt.secs)},${yAt(pctOf(pt.count, started))}`).join(' ')} fill="none"
            stroke={C.chartLine} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

          {points.map((pt, i) => (
            <circle key={i} cx={xAt(pt.secs)} cy={yAt(pctOf(pt.count, started))} r={hover === i ? 5 : 3}
              fill={hover === i ? C.cta : C.chartLine} />
          ))}

          {/* Invisible hit strips — these make the tooltip work on touch as
              well as hover, which matters because this is read on a phone. */}
          {points.map((pt, i) => (
            <rect key={`h${i}`}
              x={xAt(pt.secs) - gap / 2}
              y={0}
              width={gap}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onTouchStart={() => setHover(i)}
            />
          ))}

          {points.map((pt, i) => (i % step === 0 || i === n - 1) ? (
            <text key={`x${i}`} x={xAt(pt.secs)} y={plotH + 18} textAnchor="middle" fontSize="11" fill={C.accent}>
              {pt.label}
            </text>
          ) : null)}

          <text x={plotW / 2} y={plotH + 36} textAnchor="middle" fontSize="10" fill={C.accent}>
            Time actually seen
          </text>
        </g>
      </svg>

      {tip && (
        <div data-dropoff-tip="" style={{
          position: 'absolute', top: 4,
          left: Math.min(Math.max(0, M.left + xAt(tip.secs) - TIP_W / 2), Math.max(0, width - TIP_W)),
          width: TIP_W, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
          padding: '7px 9px', fontSize: 11, color: C.text, pointerEvents: 'none', lineHeight: 1.45,
          boxShadow: '0 4px 14px rgba(61,31,2,0.13)',
        }}>
          <div style={{ fontWeight: 700, color: C.heading }}>{tip.label} or more seen</div>
          <div>
            <strong>{pctOf(tip.count, started)}%</strong> of people who started
            <span style={{ color: C.accent }}> ({tip.count} of {started})</span>
          </div>
          <div>
            <strong>{pctOf(tip.count, landed)}%</strong> of everyone who landed
            <span style={{ color: C.accent }}> ({tip.count} of {landed})</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── MODAL ────────────────────────────────────────────────────────────────────
export const Modal = ({ title, subtitle, onClose, children }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  // Escape to close, and Tab cycles inside the panel so focus cannot wander
  // behind the modal.
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab') return;
    const f = panelRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!f || !f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [onClose]);

  // Lock the page behind the modal, and restore whatever overflow it had.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={onKeyDown}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(42,20,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12, fontFamily: FONT,
      }}
    >
      <div
        ref={panelRef}
        style={{
          background: C.card, borderRadius: 14, border: `1px solid ${C.border}`,
          width: 'min(1100px, 100%)', maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto', boxShadow: '0 18px 50px rgba(42,20,0,0.28)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '16px 18px 12px', borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.heading }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: C.accent, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0, width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${C.border}`, background: C.bg, color: C.heading,
              fontSize: 16, lineHeight: 1, padding: 0,
            }}
          >×</button>
        </div>
        <div style={{ padding: '14px 18px 20px' }}>{children}</div>
      </div>
    </div>
  );
};

// ── PUBLIC: the quiet link plus the modal it opens ───────────────────────────
// curve   — the endpoint's `seenCurve` for this video (and this window of it)
// landed  — the card's own base: the "of N unique visitors" its bars divide by
// startedNote — what "started" means for this video, said on the chart's face
export const DropOffLink = ({ title, curve, landed, startedNote }) => {
  const [open, setOpen] = useState(false);
  const linkRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    linkRef.current?.focus();   // focus goes back to where it came from
  }, []);

  const started = curve?.started ?? 0;
  const points = curve?.points || [];
  const marker = (curve?.milestones || []).find(m => m.frac === 0.95);
  const anyData = started > 0 && points.some(p => p.count > 0);

  return (
    <>
      <button
        ref={linkRef}
        onClick={() => setOpen(true)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          font: 'inherit', fontSize: 10, color: C.accent,
          textDecoration: 'underline', textUnderlineOffset: 2,
        }}
      >View drop-off</button>

      {open && (
        <Modal
          title={title}
          subtitle="Share still watching at each 30-second mark, by time actually seen — the same measure as the card's bars"
          onClose={close}
        >
          {/* Both bases lead, before the chart. At these volumes they are the
              most important numbers on the screen. */}
          <div data-dropoff-bases="" style={{ fontSize: 12, color: C.heading, marginBottom: 4 }}>
            Based on <strong>{started}</strong> {started === 1 ? 'person' : 'people'} who started watching
            {' '}· of <strong>{landed}</strong> unique {landed === 1 ? 'visitor' : 'visitors'} who landed
          </div>
          <div style={{ fontSize: 10.5, color: C.accent, marginBottom: anyData ? 10 : 0, lineHeight: 1.5 }}>
            The line is the share of the {started}; each point also gives the share of all {landed}.
            {startedNote ? ` ${startedNote}` : ''} Skipping ahead and rewatching don't count.
          </div>

          {started > 0 && started < SMALL_SAMPLE && (
            <div style={{
              fontSize: 11, color: C.text, background: C.amberBg,
              border: `1px solid ${C.border}`, borderRadius: 8,
              padding: '8px 10px', marginBottom: 12, lineHeight: 1.5,
            }}>
              Too small a sample to read anything into — {started} {started === 1 ? 'person' : 'people'}.
              One person leaving moves this line by {Math.round(100 / started)} percentage points.
              Treat it as a shape to watch, not a finding, until a few hundred people have watched.
            </div>
          )}

          {anyData ? (
            <RetentionChart points={points} started={started} landed={landed}
              lengthSecs={curve.lengthSecs} marker={marker ? { ...marker, label: '95%+ seen' } : null} />
          ) : (
            <div style={{ fontSize: 12, color: C.accent, padding: '18px 0', lineHeight: 1.6 }}>
              No watch data in this date range yet — there is nothing to chart.
              {started === 0 && ' Nobody has started watching this video in the selected period.'}
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
